import React, { useState } from 'react';
import { theme } from '../../../theme';

interface DataPoint {
  day: number;
  revenue: number;
}

interface RevenueLineChartProps {
  data: DataPoint[];
  timeRange: '7d' | '30d' | '90d';
  onTimeRangeChange: (range: '7d' | '30d' | '90d') => void;
  onExport?: () => void;
}

const RevenueLineChart: React.FC<RevenueLineChartProps> = ({
  data,
  timeRange,
  onTimeRangeChange,
  onExport,
}) => {
  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1);
  const chartHeight = 400;
  const chartPadding = 20;
  const barGap = 8;

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '18px',
    border: `1px solid ${theme.colors.border}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '24px',
    color: theme.colors.text,
    letterSpacing: '1px',
    margin: 0,
  };

  const timeTabsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '4px',
    backgroundColor: theme.colors.background,
    borderRadius: '8px',
    padding: '4px',
    height: '36px',
  };

  const getTabStyle = (range: '7d' | '30d' | '90d'): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '8px',
    backgroundColor: timeRange === range ? theme.colors.primary : 'transparent',
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: timeRange === range ? '600' : '400',
    color: timeRange === range ? '#000' : theme.colors.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const chartAreaStyle: React.CSSProperties = {
    height: `${chartHeight}px`,
    position: 'relative',
  };

  const chartBackgroundStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.background,
    borderRadius: '12px',
    padding: '20px',
    position: 'relative',
  };

  const gridLinesStyle: React.CSSProperties = {
    position: 'absolute',
    top: '20px',
    left: '20px',
    right: '20px',
    bottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  const gridLineStyle: React.CSSProperties = {
    width: '100%',
    height: '1px',
    backgroundColor: theme.colors.border,
  };

  const barsContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: '20px',
    left: '20px',
    right: '20px',
    bottom: '20px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: `${barGap}px`,
    padding: '20px 0',
  };

  const getBarStyle = (revenue: number, index: number): React.CSSProperties => {
    const heightPercentage = (revenue / maxRevenue) * 100;
    const isHighlighted = index === data.length - 1;

    return {
      flex: 1,
      height: `${heightPercentage}%`,
      backgroundColor: theme.colors.primary,
      borderRadius: '4px 4px 0 0',
      opacity: isHighlighted ? 1 : 0.7,
      transition: 'opacity 0.2s, height 0.3s',
      cursor: 'pointer',
    };
  };

  const labelsStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 20px',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    color: theme.colors.textSecondary,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>REVENUE HISTÓRICO</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Time Range Tabs */}
          <div style={timeTabsStyle}>
            <button style={getTabStyle('7d')} onClick={() => onTimeRangeChange('7d')}>
              7 días
            </button>
            <button style={getTabStyle('30d')} onClick={() => onTimeRangeChange('30d')}>
              30 días
            </button>
            <button style={getTabStyle('90d')} onClick={() => onTimeRangeChange('90d')}>
              90 días
            </button>
          </div>
          {onExport && (
            <button
              onClick={onExport}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.primary}`,
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                fontWeight: '600',
                color: theme.colors.primary,
                cursor: 'pointer',
              }}
            >
              Exportar CSV
            </button>
          )}
        </div>
      </div>

      {/* Chart Area */}
      <div style={chartAreaStyle}>
        <div style={chartBackgroundStyle}>
          {/* Grid Lines */}
          <div style={gridLinesStyle}>
            <div style={gridLineStyle} />
            <div style={gridLineStyle} />
            <div style={gridLineStyle} />
            <div style={gridLineStyle} />
          </div>

          {/* Bars */}
          <div style={barsContainerStyle}>
            {data.map((point, index) => (
              <div
                key={point.day}
                style={getBarStyle(point.revenue, index)}
                title={`Día ${point.day}: $${point.revenue.toLocaleString()}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Labels */}
      <div style={labelsStyle}>
        <span style={labelStyle}>1</span>
        <span style={labelStyle}>5</span>
        <span style={labelStyle}>10</span>
        <span style={labelStyle}>15</span>
        <span style={labelStyle}>20</span>
        <span style={labelStyle}>25</span>
        <span style={labelStyle}>30</span>
      </div>
    </div>
  );
};

export default RevenueLineChart;
